import { useTranslation } from 'next-i18next';

import { SubLayoutButton } from '../types/GlobalTypes';
import { StoreAssetSubCategory } from '../types/StoreTypes';

export const useStoreSubLayoutLeftButtons = (): SubLayoutButton[] => {
  const { t } = useTranslation(['store']);
  return [
    {
      name: 'all',
      pageName: t('store:store_asset_all'),
      link: '/store',
    },
    {
      name: 'map',
      pageName: t('store:store_asset_map'),
      link: '/store/assets?type=map',
    },
    {
      name: 'object',
      pageName: t('store:store_asset_object'),
      link: '/store/assets?type=object',
    },
    {
      name: 'app',
      pageName: t('store:store_asset_app'),
      link: '/store/assets?type=app',
    },
    {
      name: 'game',
      pageName: t('store:store_asset_game'),
      link: '/store/assets?type=game',
    },
  ];
};

export const useStoreSubLayoutRightButtons = (
  isEdu?: boolean,
): Array<SubLayoutButton> => {
  const { t } = useTranslation(['store']);
  return [
    {
      name: 'like',
      pageName: t('store:store_asset_liked'),
      link: '/store/save?type=like',
    },
    {
      name: 'purchased',
      pageName: isEdu
        ? t('store:store_asset_purchased_edu')
        : t('store:store_asset_purchased'),
      link: '/store/save?type=purchased',
    },
  ];
};

export const StoreCategoryNumber: Readonly<Record<string, number>> = {
  map: 1,
  object: 2,
  app: 3,
  game: 4,
  character: 5,
};

export const StoreCategoryString: Readonly<Record<string, string>> = {
  map: 'map',
  object: 'object',
  app: 'app',
  game: 'game',
  character: 'character',
};

export const StoreImageType: Readonly<Record<string, number>> = {
  none: 0,
  icon: 1,
  thumbnail: 2,
  preview: 3,
  objectImage: 4,
};

/**
 * 에듀 에셋의 경우 카테고리에서 6,7,8,9 로 분기하고 있어 Length 나머지 연산을 진행해야 함.
 * @param category 서버에서 받아오는 카테고리 값
 * @returns 에듀와 일반 에셋을 같이 관리할 카테고리 number
 */
export const getStoreCategoryIndex = (category?: number) => {
  if (!category) {
    return 0;
  }

  const categoryLength = Object.keys(StoreCategoryNumber).length;
  return category % categoryLength;
};

/**
 * StoreCategory의 역변환 오브젝트
 */
export const StoreCategoryName: Record<number, string> = Object.keys(
  StoreCategoryNumber,
).reduce((obj, key) => {
  obj[StoreCategoryNumber[key]] = key;
  return obj;
}, {} as Record<number, string>);

export const useStoreAppSubCategories = (): StoreAssetSubCategory[] => {
  const { t } = useTranslation(['store']);
  return [
    { icon: '', name: t('store:store_asset_all'), value: 0 },
    { icon: '✍️', name: t('store:store_category_productivity'), value: 1 },
    { icon: '😆', name: t('store:store_category_event'), value: 2 },
    { icon: '🏫', name: t('store:store_category_education'), value: 3 },
    { icon: '🎉', name: t('store:store_category_entertainment'), value: 4 },
    { icon: '🛍️', name: t('store:store_category_commerce'), value: 5 },
    { icon: '', name: t('store:store_category_etc'), value: 6 },
  ];
};

/**
 * 에셋의 리뷰 진행 상황
 */
export enum StoreReviewState {
  preview = 0,
  required = 1,
  completed = 2,
  rejected = 3,
  unofficial = 4,
  official = 5,
}

export const StoreMaximumPrice = 1_000_000_000;

export const StoreQueryKeys = {
  /**
   * 에셋 리스트를 가져오기 위한 쿼리 키
   */
  ASSET_LIST_GET: 'assets',
  /**
   * 찜한 또는 구매한 에셋 리스트를 가져오기 위한 키
   */
  ASSET_SAVED_LIST_GET: 'save-assets',
  /**
   * 상세 에셋 정보를 가져오기 위한 키
   */
  ASSET_DETAIL_GET: 'asset-detail',
  /**
   * 편집을 위해 내가 가진 에셋에 대한 정보를 가져오기 위한 키
   */
  MY_ASSET_DETAIL_GET: 'my-asset-detail',
  /**
   * 상세 에셋에 대한 업로드를 위한 키
   */
  ASSET_DETAIL_UPLOAD: 'asset-detail-upload',
  /**
   * 상세 에셋에 대한 삭제를 위한 키
   */
  ASSET_DETAIL_DELETE: 'asset-detail-delete',
  /**
   * 특정 에셋의 리뷰 리스트를 가져오기 위한 키
   */
  ASSET_REVIEW_LIST_GET: 'asset-comments',
  /**
   * 특정 에셋의 리뷰를 추가해주기 위한 키
   */
  ASSET_REVIEW_DETAIL_CREATE: 'asset-comment-detail-create',
  /**
   * 특정 에셋의 좋아요, 좋아요 취소를 반영하기 위한 키
   */
  LIKE_ASSET: 'like-asset',
  /**
   * 특정 무료 에셋을 구매하기 위한 키
   */
  PURCHASE_ASSET: 'purchase-asset',

  RECOMMENDED_LIST: 'recommended-list',
  POPULAR_LIST: 'popular-list',
};
